// Stack — Blöcke präzise stapeln. Überstand wird abgeschnitten.
// Voxel-Materialien (Erde, Stein, Sand, Holz, Gras) prozedural gezeichnet.
import { useRef, useEffect } from "react";
import { drawTexturedBlock, drawBlockText, setupCanvas } from "./pixelArt";

const H = 640;
const BASE_ASPECT = 420 / 640;
const BLOCK_H = 32;
const BASE_W = 220;
const GROUND_H = 64;
const BASE_TOP = H - GROUND_H - BLOCK_H; // Welt-Y der Oberkante des Basisblocks
const PERFECT_TOLERANCE = 6;

const MATERIALS = [
  { base: "#8a5a2b", light: "#a06a35", dark: "#6e4620" }, // Erde
  { base: "#8d8d8d", light: "#a5a5a5", dark: "#6e6e6e" }, // Stein
  { base: "#d9c078", light: "#e8d494", dark: "#b89f56" }, // Sand
  { base: "#7a5230", light: "#8f6440", dark: "#5e3d20" }, // Holz
  { base: "#6abe30", light: "#7ed641", dark: "#4e9420" }, // Gras
];

export default function StackGame({ onGameOver, onStart, autoStart = false }) {
  const canvasRef = useRef(null);
  const onGameOverRef = useRef(onGameOver);
  onGameOverRef.current = onGameOver;
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;

  useEffect(() => {
    const canvas = canvasRef.current;
    // Spielfeldbreite ans Seitenverhältnis des Containers anpassen: auf dem
    // Desktop wird das Feld breiter (mehr Welt links/rechts), nichts wird
    // verzerrt — die logische Höhe bleibt immer gleich.
    const availW = canvas.parentElement?.clientWidth || 420;
    const availH = canvas.parentElement?.clientHeight || H;
    const aspect = Math.min(Math.max(availW / availH, BASE_ASPECT), 3.2);
    const W = Math.round(H * aspect);
    const ctx = setupCanvas(canvas, W, H);

    const state = {
      phase: "ready",
      stack: [], // {x, w, mat, seed}
      cur: null, // {x, w, dir, speed}
      score: 0,
      cameraY: 0,
      lastTime: 0,
      flash: 0, // Perfekt-Treffer-Feedback
    };

    const reset = () => {
      state.stack = [
        { x: (W - BASE_W) / 2, w: BASE_W, mat: 1, seed: 12345 },
      ];
      state.score = 0;
      state.cameraY = 0;
      state.flash = 0;
      spawnBlock();
    };

    function spawnBlock() {
      const top = state.stack[state.stack.length - 1];
      const fromLeft = state.stack.length % 2 === 0;
      state.cur = {
        x: fromLeft ? -top.w : W,
        w: top.w,
        dir: fromLeft ? 1 : -1,
        speed: Math.min(380, 150 + state.stack.length * 5),
      };
    }

    const drop = () => {
      const cur = state.cur;
      const below = state.stack[state.stack.length - 1];
      const left = Math.max(cur.x, below.x);
      const right = Math.min(cur.x + cur.w, below.x + below.w);
      const overlap = right - left;

      if (overlap <= 4) {
        state.phase = "over";
        if (onGameOverRef.current) onGameOverRef.current(state.score);
        return;
      }

      const perfect = Math.abs(cur.x - below.x) <= PERFECT_TOLERANCE;
      const newBlock = perfect
        ? { x: below.x, w: cur.w }
        : { x: left, w: overlap };

      if (perfect) state.flash = 1;

      state.stack.push({
        ...newBlock,
        mat: state.stack.length % MATERIALS.length,
        seed: (state.stack.length + 7) * 9973,
      });
      state.score++;
      spawnBlock();
    };

    const worldToScreenY = (layer) =>
      BASE_TOP - layer * BLOCK_H + state.cameraY;

    const draw = () => {
      // Himmel: wird mit Höhe langsam dunkler (Weltraum-Feeling)
      const height = state.stack.length;
      const t = Math.min(1, height / 120);
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, t > 0.5 ? "#101528" : "#4a90c2");
      grad.addColorStop(1, "#87c5ea");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // Blocky Wolken (parallax zur Kamera)
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      const cloudOffset = (state.cameraY * 0.3) % (H + 100);
      for (let i = 0; i < 4; i++) {
        const cy = ((i * 190 + cloudOffset) % (H + 100)) - 50;
        const cx = (i * 137) % (W - 100);
        ctx.fillRect(cx, cy, 70, 16);
        ctx.fillRect(cx + 12, cy - 12, 46, 12);
      }

      // Boden (nur solange sichtbar)
      const groundY = H - GROUND_H + state.cameraY;
      if (groundY < H) {
        drawTexturedBlock(ctx, 0, groundY, W, GROUND_H + 40, "#8a5a2b", "#a06a35", "#6e4620", 42);
        ctx.fillStyle = "#6abe30";
        ctx.fillRect(0, groundY, W, 8);
      }

      // Turm
      for (let i = 0; i < state.stack.length; i++) {
        const b = state.stack[i];
        const y = worldToScreenY(i);
        if (y < -BLOCK_H || y > H) continue;
        const m = MATERIALS[b.mat];
        drawTexturedBlock(ctx, b.x, y, b.w, BLOCK_H, m.base, m.light, m.dark, b.seed);
      }

      // Wandernder Block
      if (state.phase === "running" && state.cur) {
        const m = MATERIALS[state.stack.length % MATERIALS.length];
        const y = worldToScreenY(state.stack.length);
        drawTexturedBlock(ctx, state.cur.x, y, state.cur.w, BLOCK_H, m.base, m.light, m.dark, 777);
      }

      // Perfekt-Feedback
      if (state.flash > 0) {
        drawBlockText(ctx, "PERFEKT!", W / 2, 110, 26, "#ffe27a");
        state.flash -= 0.02;
      }

      drawBlockText(ctx, `${state.score}`, W / 2, 50, 36);

      if (state.phase === "ready") {
        ctx.fillStyle = "rgba(10, 15, 25, 0.55)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "STACK", W / 2, H / 2 - 40, 46);
        drawBlockText(ctx, "Tippen / Leertaste zum Stapeln", W / 2, H / 2 + 10, 16);
      } else if (state.phase === "over") {
        ctx.fillStyle = "rgba(10, 15, 25, 0.55)";
        ctx.fillRect(0, 0, W, H);
        drawBlockText(ctx, "GAME OVER", W / 2, H / 2 - 40, 40);
        drawBlockText(ctx, `${state.score} Blöcke`, W / 2, H / 2 + 5, 24);
        drawBlockText(ctx, "Tippen für Neustart", W / 2, H / 2 + 45, 15);
      }
    };

    let raf;
    const loop = (now) => {
      const dt = Math.min(0.05, (now - state.lastTime) / 1000);
      state.lastTime = now;

      if (state.phase === "running" && state.cur) {
        const cur = state.cur;
        cur.x += cur.dir * cur.speed * dt;
        if (cur.x <= -20 && cur.dir < 0) cur.dir = 1;
        if (cur.x + cur.w >= W + 20 && cur.dir > 0) cur.dir = -1;

        // Kamera folgt dem Turm nach oben (sanft)
        const targetCam = Math.max(0, state.stack.length * BLOCK_H - (BASE_TOP - 240));
        state.cameraY += (targetCam - state.cameraY) * Math.min(1, dt * 6);
      }

      draw();
      raf = requestAnimationFrame(loop);
    };

    const action = () => {
      if (state.phase === "running") {
        drop();
      } else {
        reset();
        state.phase = "running";
        if (onStartRef.current) onStartRef.current();
      }
    };

    const onKeyDown = (e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        action();
      }
    };
    const onPointerDown = (e) => {
      e.preventDefault();
      action();
    };

    window.addEventListener("keydown", onKeyDown);
    canvas.addEventListener("pointerdown", onPointerDown);

    state.lastTime = performance.now();
    if (autoStart) action(); // Start-Button auf der Seite: direkt losspielen
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKeyDown);
      canvas.removeEventListener("pointerdown", onPointerDown);
    };
  }, [autoStart]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-full touch-none select-none"
      style={{ imageRendering: "pixelated", objectFit: "contain" }}
    />
  );
}
